import { useEffect, useState } from "react";
import { Play } from "lucide-react";
import { toast } from "sonner";
import { api, daysAgo, today } from "@/lib/api";
import { Card, Btn, Field } from "@/components/shared";
import { Chip, money, call, when } from "./common";

const ROWS = [["opening", "Opening"], ["credits", "Credits"], ["debits", "Debits"], ["closing", "Closing"], ["matched", "Matched"], ["unmatched", "Unmatched"]];
const AGE = [["lt1d", "<1d"], ["d1_3", "1-3d"], ["d3_7", "3-7d"], ["gt7d", ">7d"]];
const TAB_FOR = { credit: "credit", evalue: "evalue", vendor_funding: "vendors", vendor_txn: "payouts", wallet: "network" };

function Tile({ t, go }) {
  return (
    <div className="np-card flex flex-col p-4" data-testid={`recon-tile-${t.key}`}>
      <div className="mb-2 flex items-center justify-between">
        <button className="font-display text-sm font-bold hover:underline" onClick={() => go(TAB_FOR[t.key])} data-testid={`recon-tile-${t.key}-open`}>{t.label}</button>
        {t.open_count > 0 || (t.key === "wallet" && t.unmatched > 0.009) ? <Chip s="OPEN" testid={`recon-tile-${t.key}-chip`} /> : <Chip s="MATCHED" testid={`recon-tile-${t.key}-chip`} />}
      </div>
      <dl className="space-y-1 text-xs">
        {ROWS.map(([k, l]) => (
          <div key={k} className="flex justify-between"><dt className="text-slate-500">{l}</dt>
            <dd className={`font-mono ${k === "unmatched" && t[k] ? "font-bold text-red-700" : ""}`} data-testid={`recon-tile-${t.key}-${k}`}>{money(t[k])}</dd></div>
        ))}
        {t.internal_transfers !== undefined && <div className="flex justify-between"><dt className="text-slate-500">Internal transfers (net 0)</dt><dd className="font-mono">{money(t.internal_transfers)}</dd></div>}
      </dl>
      <div className="mt-3 border-t border-slate-100 pt-2 text-xs">
        <button className="font-semibold text-blue-700" onClick={() => go("queues", { area: t.areas[0] })} data-testid={`recon-tile-${t.key}-items`}>
          {t.open_count} open · {money(t.open_amount)}
        </button>
        {t.unexplained > 0 && <div className="mt-1 text-amber-700">Closed — unexplained: {money(t.unexplained)}</div>}
        <div className="mt-2 flex gap-1">{AGE.map(([k, l]) => <span key={k} className={`rounded px-1.5 py-0.5 text-[10px] ${t.ageing[k] ? "bg-red-50 text-red-700" : "bg-slate-50 text-slate-400"}`}>{l}: {t.ageing[k]}</span>)}</div>
      </div>
    </div>
  );
}

export function ReconDashboard({ go }) {
  const [f, setF] = useState({ date_from: daysAgo(30), date_to: today() });
  const [d, setD] = useState(null);
  const [running, setRunning] = useState(false);
  const load = () => api.get("/admin/recon/dashboard", { params: f }).then(({ data }) => setD(data));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const run = async () => {
    setRunning(true);
    const r = await call(() => api.post("/admin/recon/run"));
    setRunning(false);
    if (r) { (r.status === "DONE" ? toast.success : toast.error)(`Recon ${r.status.toLowerCase()} · ${r.open_items ?? 0} open items${r.error ? ` · ${r.error}` : ""}`); load(); }
  };
  return (
    <div>
      <div className="np-card mb-4 flex flex-wrap items-end gap-3 p-4">
        <Field label="From"><input type="date" className="np-input" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="recon-dash-from" /></Field>
        <Field label="To"><input type="date" className="np-input" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="recon-dash-to" /></Field>
        <Btn variant="outline" onClick={load} data-testid="recon-dash-search">Refresh</Btn>
        <Btn onClick={run} loading={running} data-testid="recon-run-button"><Play className="h-4 w-4" />Run Recon</Btn>
        {d?.last_run && (
          <div className="ml-auto text-xs text-slate-500" data-testid="recon-last-run">
            Last run {when(d.last_run.started_at)} · {d.last_run.trigger} · <Chip s={d.last_run.status} /> · daily auto-run 2:00 AM IST
          </div>
        )}
      </div>
      {d?.pending_adjustments > 0 && (
        <button onClick={() => go("adjustments")} className="mb-4 w-full rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-left text-sm font-semibold text-amber-800" data-testid="recon-pending-adj-banner">
          {d.pending_adjustments} adjustment(s) waiting for main Admin approval →
        </button>
      )}
      {!d ? <div className="text-sm text-slate-400">Loading…</div> : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">{d.tiles.map((t) => <Tile key={t.key} t={t} go={go} />)}</div>
      )}
      <Card className="mt-6" title="How to work through recon">
        <ol className="list-decimal space-y-1 pl-5 text-sm text-slate-600">
          <li>Upload today&apos;s company bank statement in <b>E-value / Bank</b> (column mapping is remembered per bank).</li>
          <li>Click <b>Run Recon</b>. Exact matches link automatically; everything else lands in <b>Open Items</b>.</li>
          <li>Link, resolve with reason + evidence, or raise an adjustment (main Admin approves).</li>
          <li>Recover overdue credits in <b>Credit Recovery</b>; check vendor balance in <b>Vendors</b>.</li>
        </ol>
      </Card>
    </div>
  );
}
